<template>
  <section class="awards">
    <div class="awards__media" aria-hidden="true" />
    <div class="awards__card">
      <div class="awards__bento">
        <h2 class="awards__title">
          награды <br class="awards__title-br" />победителям
        </h2>
        <div class="awards__prizes">
          <div v-for="prize in prizes" :key="prize.title" class="awards__prize">
            <p class="awards__prize-head">
              <span class="awards__prize-icon">
                <img :src="prize.icon" alt="" width="24" height="24" />
              </span>
              <span class="awards__prize-title">{{ prize.title }}</span>
            </p>
            <!-- prettier-ignore -->
            <p class="awards__prize-text">{{ prize.text }}<br v-if="prize.tail" class="awards__prize-br" />{{ prize.tail }}</p>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
// Графика подставляется фоном в стилях (см. .awards__media) — это декор,
// отдельный <img> ему не нужен.
import iconRouble from "@/assets/case-championship/icon-rouble.svg";
import iconCoffee from "@/assets/case-championship/icon-coffee.svg";

const prizes = [
  {
    title: "1 место",
    text: "Годовая именная стипендия",
    icon: iconRouble,
  },
  {
    title: "2 и 3 место",
    // Хвостовой пробел разделяет части, когда перенос отключён (мобильная
    // вёрстка): в разметке между текстом и хвостом пробела нет.
    text: "Фирменный мерч Т2 ",
    tail: "на память",
    icon: iconCoffee,
  },
];
</script>

<style lang="scss" scoped>
.awards {
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  padding: 8px;
  padding-top: 176px;
  border-radius: 32px;
  overflow: clip;
  background: var(--pink-banner-bg);

  // Отступ под увеличенную планшетную графику — см. &__media.
  @include md {
    padding-top: 220px;
  }

  // Десктопная раскладка — строго выше 1180: на планшетной ширине в строке
  // «графика + карточка» трофей обрезался по бокам.
  @include above-banner {
    flex-direction: row;
    align-items: stretch;
    justify-content: flex-end;
    padding: 0;
  }

  &__media {
    position: absolute;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 288px;
    height: 224px;
    background-image: image-set(
      url("@/assets/case-championship/awards-mobile.webp") 1x,
      url("@/assets/case-championship/awards-mobile@2x.webp") 2x
    );
    background-repeat: no-repeat;
    background-position: center;
    // Экспорт с прозрачностью: фон секции (--pink-banner-bg) просвечивает,
    // поэтому contain показывает графику целиком — без обрезки и без швов.
    background-size: contain;

    // На планшетной ширине 288×224 в блоке теряется — графика крупнее,
    // отступ под неё задан в .awards.
    @include md {
      width: 360px;
      height: 280px;
    }

    @include above-banner {
      position: relative;
      left: auto;
      transform: none;
      // В макете графика занимает ровно 632px (размер экспорта 632×268),
      // остальные 680px забирает карточка. Обе половины сжимаются
      // пропорционально, поэтому на 1181–1440 раскладка не ломается.
      flex: 0 1 632px;
      min-width: 240px;
      width: auto;
      height: auto;
      // Высота графика — всегда 100% блока: при contain картинка ужималась
      // по ширине и сверху/снизу оставались полосы. Края обрезаются.
      background-size: auto 100%;
      background-image: image-set(
        url("@/assets/case-championship/awards-desktop.webp") 1x,
        url("@/assets/case-championship/awards-desktop@2x.webp") 2x
      );
    }
  }

  &__card {
    position: relative;
    width: 100%;

    @include above-banner {
      // Карточка занимает остаток ширины секции (680px при 1440), а призы
      // внутри растягиваются поровну — по 288px, как в макете.
      flex: 0 1 680px;
      width: auto;
      min-width: 0;
      padding: 24px;
    }
  }

  &__bento {
    display: flex;
    flex-direction: column;
    // В макете на мобильном разрыв между заголовком и призами — 12px,
    // на десктопе — 16px.
    gap: 12px;
    padding: 12px;
    background: var(--colorWhite);
    border-radius: var(--radiusInner);

    @include above-banner {
      gap: 16px;
      padding: 24px;
    }
  }

  &__title {
    color: var(--colorBlack);
    font-size: var(--sizeHeading2);
    line-height: 26px;

    @include above-banner {
      line-height: 30px;
    }
  }

  &__title-br {
    // На мобильной вёрстке заголовок идёт в две строки, на десктопе — в одну.
    // Пробел стоит перед <br>: на мобильном он срезан как конец строки, на
    // десктопе (где <br> скрыт) разделяет слова — иначе «наградыпобедителям».
    // После <br> его держать нельзя: prettier заменяет пробел на перенос строки.
    @include above-banner {
      display: none;
    }
  }

  &__prizes {
    display: flex;
    flex-direction: column;
    gap: 8px;

    // Карточка на планшете во всю ширину, так что призы помещаются в строку.
    @include md {
      flex-direction: row;
      align-items: stretch;
    }
  }

  &__prize {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 16px;
    background: var(--colorPink);
    border-radius: var(--radiusOuter);
    color: var(--colorWhite);

    @include md {
      flex: 1 0 0;
      min-width: 1px;
      justify-content: space-between;
    }

    @include above-banner {
      gap: 16px;
      padding: 24px;
      border-radius: var(--radiusInner);
    }
  }

  &__prize-head {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  &__prize-icon {
    flex-shrink: 0;
    display: grid;
    place-items: center;
    width: 40px;
    height: 40px;
    background: var(--colorWhite);
    border-radius: 12px;
  }

  &__prize-title {
    font-family: var(--fontSecondary);
    font-weight: 800;
    // В макете подзаголовок карточки — 18px (--sizeHeading4 на md даёт 20px),
    // поэтому размер задан литералом: ширина «1 место» совпадает с макетом.
    font-size: 18px;
    line-height: 20px;
    text-transform: uppercase;
  }

  &__prize-text {
    font-size: var(--sizeText);
    line-height: var(--lineHeightText);

    // На макетной ширине (1440+) места хватает, поэтому текст не переносится
    // сам — строку рвёт только макетный <br>. Ниже перенос идёт по ширине
    // карточки, иначе текст в неё не влезает.
    @include xl {
      white-space: nowrap;
    }
  }

  // На десктопе «на память» стоит отдельной строкой — так в макете.
  // Ниже перенос снят: там текст переносится сам, по ширине карточки.
  &__prize-br {
    display: none;

    @include above-banner {
      display: inline;
    }
  }
}
</style>
